import { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/badge/chip";
import { Section } from "@/components/section";
import { MermaidGraph } from "@/components/mermaid-graph";
import { UserAvatar } from "@/components/user-chip";
import { UsersSelector } from "@/components/users-selector";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { putFetcher } from "@/lib/swr";
import type { App, User } from "@/types/domain";
import { refreshApps } from "./refresh-apps";

// RC 审核流程不是可编排的图,而是一条固定流程(引擎写死),App 能配的只有
// 「每个环节谁来处理」。所以这里是「只读流程图 + 配人表」:流程图按固定
// 管线画,表格每行一个环节。服务端 setRcFlow 同口径——传入的 start/edges
// 一律忽略,只收 handlers。
//
// 「发起人」是动态处理人:解析时落到各 RC 的 requester(有 BI 出数据的
// 团队配具体人,没有的配发起人自己),审批环节禁用。

const REQUESTER = "@requester";

// 环节表(与服务端 RC_FLOW_NODES / RC_FLOW_REQUIRED_HANDLERS 镜像)
const STEPS = [
  {
    key: "approve",
    label: "待审批",
    hint: "立项审批人,任一人通过/拒绝;不配 = 不走立项审批",
  },
  {
    key: "implement",
    label: "待配置",
    hint: "配置操作人:去 Firebase 配好后贴链接开实验",
    required: true,
  },
  {
    key: "experimenting",
    label: "实验中·等待数据回收",
    hint: "数据 report 回填与催办对象(如 BI);不配 = 发起人",
    requester: true,
  },
  {
    key: "deploy-approve",
    label: "部署审批",
    hint: "review 实验结果的审批人;不配 = 同立项审批人",
  },
  {
    key: "deployed",
    label: "已部署",
    hint: "部署执行与回滚回收的操作人(确认后状态才推进)",
    required: true,
  },
  {
    key: "close-approve",
    label: "回滚审批",
    hint: "review 该不该下线的审批人;不配 = 同立项审批人",
  },
  {
    key: "closed",
    label: "已回滚",
    hint: "补结论催办对象(AB 实验);不配 = 发起人",
    requester: true,
  },
] as const;

type StepKey = (typeof STEPS)[number]["key"];
type Handlers = Partial<Record<string, string[]>>;

// 固定管线示意图(只读),与服务端 RC_FLOW_PIPELINE 一致
const PIPELINE_MERMAID = [
  "flowchart LR",
  "  start((申请)) --> approve",
  "  approve[待审批]",
  "  implement[待配置]",
  "  experimenting[实验中·等待数据回收]",
  "  deployApprove[部署审批]",
  "  deployed[已部署]",
  "  archived([存档])",
  "  closed([已回滚])",
  "  rejected([被拒])",
  "  approve --> implement",
  "  approve --> rejected",
  "  implement --> experimenting",
  "  implement --> deployed",
  "  closeApprove[回滚审批]",
  "  experimenting --> deployApprove",
  "  experimenting --> closeApprove",
  "  closeApprove --> closed",
  "  deployApprove --> deployed",
  "  deployed --> archived",
  "  deployed --> closeApprove",
].join("\n");

const serialize = (h: Handlers) =>
  JSON.stringify(STEPS.map((s) => [...(h[s.key] ?? [])].sort()));

export const RcFlowSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  const { data: users } = useSWR<User[]>(["/users"]);
  const userById = new Map((users ?? []).map((u) => [String(u.id), u]));

  // 存量:合并前「等待数据回收」的处理人并进实验中节点(服务端读取同样兼容)
  const saved: Handlers = (() => {
    const h: Handlers = { ...(app.rcFlow?.handlers ?? {}) };
    const legacy = h["awaiting-data"];
    if (legacy?.length) {
      h.experimenting = [...new Set([...(h.experimenting ?? []), ...legacy])];
      delete h["awaiting-data"];
    }
    // flow 编辑器出现前只配了审批人的,展示为待审批环节的处理人
    if (!h.approve?.length && app.rcApproverIds?.length) {
      h.approve = app.rcApproverIds;
    }
    return h;
  })();

  const [draft, setDraft] = useState<Handlers | null>(null);
  const handlers = draft ?? saved;
  const dirty = serialize(handlers) !== serialize(saved);
  const missing = STEPS.filter(
    (s) => "required" in s && s.required && !handlers[s.key]?.length
  );

  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/rc-flow`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        setDraft(null);
        toast({ title: "RC flow saved" });
      },
      onError: (err: unknown) => toastError(toast, "Save failed", err),
    }
  );

  const setHandlers = (key: StepKey, ids: string[]) =>
    setDraft({ ...handlers, [key]: ids });

  const toggleRequester = (key: StepKey) => {
    const curr = handlers[key] ?? [];
    setHandlers(
      key,
      curr.includes(REQUESTER)
        ? curr.filter((id) => id !== REQUESTER)
        : [...curr, REQUESTER]
    );
  };

  return (
    <Section
      title="RC flow"
      action={
        <div className="flex items-center gap-2">
          {dirty && missing.length > 0 && (
            <span className="text-xs text-destructive">
              {missing.map((s) => s.label).join("、")}未配置处理人
            </span>
          )}
          <Button
            size="sm"
            className="h-8"
            disabled={!dirty || isMutating || missing.length > 0}
            onClick={() => trigger({ flow: { handlers } })}
          >
            {isMutating ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <p className="mt-1 text-xs text-muted-foreground">
        流程是固定的,这里只配每个环节谁来处理。
      </p>
      <div className="mt-2">
        <MermaidGraph code={PIPELINE_MERMAID} />
      </div>
      <div className="mt-3 flex flex-col gap-1">
        {STEPS.map((step) => {
          const ids = handlers[step.key] ?? [];
          const hasRequester = ids.includes(REQUESTER);
          const picked = ids
            .filter((id) => id !== REQUESTER)
            .map((id) => userById.get(id))
            .filter((u): u is User => !!u);
          const required = "required" in step && step.required;
          return (
            <div key={step.key} className="flex items-center gap-3 py-0.5">
              <Chip tone="neutral" className="w-40 shrink-0 text-center">
                {step.label}
              </Chip>
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                {step.hint}
              </span>
              <div className="flex shrink-0 items-center gap-1.5">
                {hasRequester && <Chip tone="purple">发起人</Chip>}
                {picked.map((u) => (
                  <UserAvatar key={u.id} user={u} className="h-5 w-5" />
                ))}
                {!ids.length && (
                  <span
                    className={`text-xs ${
                      required ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {required ? "未配置" : "—"}
                  </span>
                )}
                {"requester" in step && step.requester && (
                  <Button
                    variant={hasRequester ? "secondary" : "outline"}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => toggleRequester(step.key)}
                  >
                    发起人
                  </Button>
                )}
                <UsersSelector
                  selectedUsers={picked.map((u) => ({
                    id: u.id,
                    name: u.name,
                  }))}
                  onContinue={(users) =>
                    setHandlers(step.key, [
                      ...(hasRequester ? [REQUESTER] : []),
                      ...users.map((u) => String(u.id)),
                    ])
                  }
                  buttonText={picked.length ? "Edit" : "选人"}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export default RcFlowSection;
